/**
 * Axios instance with JWT Bearer + 401 refresh queue.
 *
 * PORTED FROM mirim/frontend/src/services/apiService.ts — adapted to:
 *  - Bearer-only (no cookie mode; per CLAUDE.md rule #9)
 *  - active_tenant_id-aware refresh endpoint
 *  - tighter typing
 *
 * The refresh-queue logic is non-trivial: when an access token expires and
 * multiple requests fail with 401 simultaneously, only ONE refresh fires.
 * The other requests wait on `failedQueue` and replay with the new token.
 * Without this, a busy page could trigger 5+ parallel refresh requests
 * and rate-limit itself out.
 */

import axios, {
  AxiosError,
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
  isAxiosError,
} from "axios";

// Re-exports so app code never imports from "axios" directly (lint-enforced).
// `AxiosError` for instanceof checks, `isAxiosError` as the type guard.
export { AxiosError, isAxiosError };
export type { AxiosResponse, InternalAxiosRequestConfig };

/**
 * Best-effort human message from a failed API call. Prefers DRF's `{detail}`,
 * then the first field error (`{rate: ["..."]}`), then the HTTP status, and
 * finally the caller-supplied translated fallback. Never throws — safe to call
 * from any mutation `onError` / catch block to surface a save failure.
 */
export function apiErrorMessage(err: unknown, fallback: string): string {
  if (isAxiosError(err)) {
    const data = err.response?.data;
    if (data && typeof data === "object" && !Array.isArray(data)) {
      const rec = data as Record<string, unknown>;
      if (typeof rec.detail === "string") return rec.detail;
      const first = Object.values(rec)[0];
      if (Array.isArray(first) && typeof first[0] === "string") return first[0];
      if (typeof first === "string") return first;
    }
    if (err.response?.status) return `${fallback} (HTTP ${err.response.status})`;
    if (err.request) return fallback; // request made, no response (network/CORS)
  }
  return fallback;
}

import { clearTokens, getAccessToken, getRefreshToken, setTokens } from "@/lib/auth";

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";

export const api: AxiosInstance = axios.create({
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json" },
  // Cross-domain Bearer flow — never send cookies
  withCredentials: false,
});

// --- Refresh-queue state ---
let isRefreshing = false;
type QueueItem = {
  resolve: (token: string) => void;
  reject: (err: unknown) => void;
};
let failedQueue: QueueItem[] = [];

function processQueue(error: unknown, token: string | null = null) {
  failedQueue.forEach((p) => {
    if (error) p.reject(error);
    else p.resolve(token!);
  });
  failedQueue = [];
}

// --- Request interceptor: Bearer + offline early-reject + FormData fix ---
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Reject early when offline so the user sees a meaningful error fast.
    // Mirim lesson: silently failing fetches in offline mode was confusing.
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      return Promise.reject(new AxiosError("Offline — no network", "ERR_OFFLINE", config));
    }

    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // axios v1 sets Content-Type: application/json by default. With FormData,
    // we need axios to set the multipart boundary itself — strip the header.
    // axios v1 uses AxiosHeaders (a class), so use the .delete() method,
    // not the JS `delete` operator (which doesn't work on class instances).
    if (config.data instanceof FormData) {
      config.headers.delete("Content-Type");
    }

    return config;
  },
  (err) => Promise.reject(err)
);

// --- Response interceptor: 401 → refresh queue → retry ---
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as
      | (InternalAxiosRequestConfig & { _retry?: boolean })
      | undefined;

    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !originalRequest.url?.includes("/auth/refresh") &&
      !originalRequest.url?.includes("/auth/login")
    ) {
      // If a refresh is already in flight, queue this request
      if (isRefreshing) {
        return new Promise<string>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refresh = getRefreshToken();
        if (!refresh) {
          throw new Error("No refresh token");
        }

        // Use raw axios (not `api`) to avoid recursive interceptor on refresh
        const r = await axios.post<{ access: string; refresh?: string }>(
          `${API_BASE}/v1/auth/refresh/`,
          { refresh },
          { headers: { "Content-Type": "application/json" } }
        );

        const newAccess = r.data.access;
        const newRefresh = r.data.refresh ?? refresh;
        setTokens(newAccess, newRefresh);
        processQueue(null, newAccess);

        originalRequest.headers.Authorization = `Bearer ${newAccess}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        clearTokens();
        // Soft redirect to login (only in browser context)
        if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
          window.location.href = "/login";
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);
